import React, { createContext, useState, useContext, useCallback, useRef, ReactNode } from 'react';
import { mergeCollectionRequestExecutionStatus } from '../utils/collectionExecutionReport';
import { releaseCollectionExecution } from '../utils/executionGate';

/**
 * Global execution state for collection runs. Execution is owned by this context,
 * not by the Collections page, so it continues when the user navigates away (e.g. to .env, Swagger).
 * UI only subscribes to and displays this state.
 */
export type CollectionRequestStatus = 'not-executed' | 'pass' | 'fail' | 'partial';

export interface ActiveCollectionExecution {
  collectionId: number;
  collectionName?: string;
  status: 'running' | 'completed' | 'stopped';
  /** Total requests (or logical steps) for progress */
  total: number;
  /** Current index (0-based) for "X of Y" */
  current: number;
  currentRequestId: number | null;
  currentRequestName: string | null;
  /** Round-robin: which row index is running (for Data Sets table highlight) */
  rowIndex: number | null;
  requestStatus: Record<number, CollectionRequestStatus>;
  message: { text: string; type: 'success' | 'error' } | null;
  error: string | null;
  /** Root + nested sub-collection IDs included in this run (for cross-module locking). */
  executingCollectionIds?: number[];
}

export interface CollectionExecutionContextType {
  activeExecution: ActiveCollectionExecution | null;
  setActiveExecution: (state: ActiveCollectionExecution | null | ((prev: ActiveCollectionExecution | null) => ActiveCollectionExecution | null)) => void;
  updateProgress: (updates: Partial<Pick<ActiveCollectionExecution, 'current' | 'currentRequestId' | 'currentRequestName' | 'rowIndex'>>) => void;
  updateRequestStatus: (requestId: number, status: CollectionRequestStatus) => void;
  updateMessage: (message: ActiveCollectionExecution['message']) => void;
  updateError: (error: string | null) => void;
  finishActiveExecution: (collectionId: number, finalState: Pick<ActiveCollectionExecution, 'status' | 'message' | 'error'>) => void;
  /** Ref used by the run loop to check for user-requested stop. Do NOT set on route change / unmount. */
  executionAbortedRef: React.MutableRefObject<boolean>;
  requestStop: () => void;
}

const CollectionExecutionContext = createContext<CollectionExecutionContextType | undefined>(undefined);

export const CollectionExecutionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeExecution, setActiveExecutionState] = useState<ActiveCollectionExecution | null>(null);

  const setActiveExecution = useCallback((state: ActiveCollectionExecution | null | ((prev: ActiveCollectionExecution | null) => ActiveCollectionExecution | null)) => {
    setActiveExecutionState((prev) => {
      const next = typeof state === 'function' ? state(prev) : state;
      if (prev?.status === 'running' && prev.collectionId > 0) {
        const stillSameRun =
          next != null &&
          next.collectionId === prev.collectionId &&
          next.status === 'running';
        if (!stillSameRun) {
          releaseCollectionExecution(prev.collectionId);
        }
      }
      return next;
    });
  }, []);

  const updateProgress = useCallback((updates: Partial<Pick<ActiveCollectionExecution, 'current' | 'currentRequestId' | 'currentRequestName' | 'rowIndex'>>) => {
    setActiveExecutionState((prev) => {
      if (!prev || prev.status !== 'running') return prev;
      return { ...prev, ...updates };
    });
  }, []);

  const updateRequestStatus = useCallback((requestId: number, status: CollectionRequestStatus) => {
    setActiveExecutionState((prev) => {
      if (!prev || prev.status !== 'running') return prev;
      const next =
        status === 'not-executed'
          ? status
          : mergeCollectionRequestExecutionStatus(prev.requestStatus[requestId], status);
      return {
        ...prev,
        requestStatus: { ...prev.requestStatus, [requestId]: next },
      };
    });
  }, []);

  const updateMessage = useCallback((message: ActiveCollectionExecution['message']) => {
    setActiveExecutionState((prev) => {
      if (!prev) return prev;
      return { ...prev, message };
    });
  }, []);

  const updateError = useCallback((error: string | null) => {
    setActiveExecutionState((prev) => {
      if (!prev) return prev;
      return { ...prev, error };
    });
  }, []);

  const finishActiveExecution = useCallback((collectionId: number, finalState: Pick<ActiveCollectionExecution, 'status' | 'message' | 'error'>) => {
    setActiveExecutionState((prev) => {
      if (!prev || prev.collectionId !== collectionId) return prev;
      releaseCollectionExecution(collectionId);
      return { ...prev, ...finalState, status: finalState.status };
    });
  }, []);

  const executionAbortedRef = useRef(false);
  const requestStop = useCallback(() => {
    executionAbortedRef.current = true;
    setActiveExecutionState((prev) => {
      if (prev?.collectionId) releaseCollectionExecution(prev.collectionId);
      return prev;
    });
  }, []);

  const value: CollectionExecutionContextType = {
    activeExecution,
    setActiveExecution,
    updateProgress,
    updateRequestStatus,
    updateMessage,
    updateError,
    finishActiveExecution,
    executionAbortedRef,
    requestStop,
  };

  return (
    <CollectionExecutionContext.Provider value={value}>
      {children}
    </CollectionExecutionContext.Provider>
  );
};

export function useCollectionExecution(): CollectionExecutionContextType {
  const context = useContext(CollectionExecutionContext);
  if (context === undefined) {
    throw new Error('useCollectionExecution must be used within a CollectionExecutionProvider');
  }
  return context;
}
